import {
  type ClientProfile,
  clientProfileOptions,
  myTasksQueryKey,
  type Task,
  taskByIdOptions,
  taskEventsOptions,
  taskEventsQueryKey,
  tasksQueryKey,
  useCurrentUserQuery,
} from '@cartwheel/api/v1'
import { Button, Dialog, LoadingView } from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { useSessionStorage } from '../utilities/use-session-storage'
import type { TaskDrawerCloseReason } from './task-analytics'
import { TaskClaimControl } from './task-claim-control'
import { TaskClientCard } from './task-client-card'
import { TaskDrawerFrame } from './task-drawer-frame'
import { TaskGuardianCard } from './task-guardian-card'
import { TaskHistory } from './task-history'
import { TaskMetadataCard } from './task-metadata-card'
import { TaskWorkspace } from './task-workspace'
import { useTaskDrawerAnalytics } from './use-task-drawer-analytics'

const POLL_INTERVAL_MS = 5000

export type TaskDrawerBlockedNavigation = {
  proceed: () => void
  reset: () => void
}

export function TaskDrawer({
  taskId,
  onClose,
  blockedNavigation,
  onUnsentNoteChange,
}: {
  taskId: string
  onClose: () => void
  blockedNavigation?: TaskDrawerBlockedNavigation
  onUnsentNoteChange?: (hasUnsentNote: boolean) => void
}) {
  const queryClient = useQueryClient()
  const currentUserQuery = useCurrentUserQuery()

  const taskQuery = useQuery({
    ...taskByIdOptions({ path: { id: taskId } }),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const status = query.state.data?.task?.status
      return status === 'open' ? POLL_INTERVAL_MS : false
    },
  })

  const task = taskQuery.data?.task
  const taskStatus = task?.status
  const trackDrawerClose = useTaskDrawerAnalytics(task)

  useEffect(() => {
    if (taskStatus === 'completed' || taskStatus === 'canceled') {
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: taskEventsQueryKey({ path: { task_id: taskId } }),
      })
    }
  }, [taskStatus, taskId, queryClient])

  const closeDrawer = (reason: TaskDrawerCloseReason) => {
    trackDrawerClose(reason)
    onClose()
  }

  if (taskQuery.isPending || currentUserQuery.isPending) {
    return <LoadingTaskDrawer onClose={() => closeDrawer('user_closed')} />
  }

  if (taskQuery.error || currentUserQuery.error || !task) {
    return <ErrorTaskDrawer onClose={() => closeDrawer('user_closed')} />
  }

  return (
    <TaskDetailPanel
      key={`${currentUserQuery.data.user.id}:${task.id}`}
      task={task}
      currentUserId={currentUserQuery.data.user.id}
      onUserClose={() => closeDrawer('user_closed')}
      onActionCompleted={() => closeDrawer('action_completed')}
      onActionCompletedNavigation={() => trackDrawerClose('action_completed')}
      blockedNavigation={blockedNavigation}
      onUnsentNoteChange={onUnsentNoteChange}
    />
  )
}

function LoadingTaskDrawer({ onClose }: { onClose: () => void }) {
  return (
    <TaskDrawerFrame onClose={onClose} title="Loading task…">
      <div className="p-6">
        <LoadingView title="Loading task…" />
      </div>
    </TaskDrawerFrame>
  )
}

function ErrorTaskDrawer({ onClose }: { onClose: () => void }) {
  return (
    <TaskDrawerFrame onClose={onClose} title="Task details">
      <div className="p-6 text-brick-light">
        <h2 className="text-lg font-semibold">Unable to load task</h2>
        <p className="mt-1 text-sm">Please close the drawer and try again.</p>
      </div>
    </TaskDrawerFrame>
  )
}

function TaskDetailPanel({
  task,
  currentUserId,
  onUserClose,
  onActionCompleted,
  onActionCompletedNavigation,
  blockedNavigation,
  onUnsentNoteChange,
}: {
  task: Task
  currentUserId: string
  onUserClose: () => void
  onActionCompleted: () => void
  onActionCompletedNavigation: () => void
  blockedNavigation?: TaskDrawerBlockedNavigation
  onUnsentNoteChange?: (hasUnsentNote: boolean) => void
}) {
  const clientQuery = useQuery({
    ...clientProfileOptions({ path: { client_id: task.client_id ?? '' } }),
    enabled: Boolean(task.client_id),
  })
  const client: ClientProfile | undefined = clientQuery.data
  const [noteDraft, setNoteDraft, removeNoteDraft] = useSessionStorage<string>({
    key: `task-note-draft:${currentUserId}:${task.id}`,
    defaultValue: '',
  })
  const [pendingCloseReason, setPendingCloseReason] = useState<'user_closed' | 'action_completed'>()
  const [actionWasCompleted, setActionWasCompleted] = useState(false)
  const hasUnsentNote = Boolean(noteDraft.trim())

  useEffect(() => {
    onUnsentNoteChange?.(hasUnsentNote)
    return () => onUnsentNoteChange?.(false)
  }, [hasUnsentNote, onUnsentNoteChange])

  const eventsQuery = useQuery(taskEventsOptions({ path: { task_id: task.id } }))
  const events = eventsQuery.data?.events ?? []
  const canWork = task.status !== 'open' || task.capabilities.can_complete

  const requestUserClose = () => {
    if (hasUnsentNote) {
      setPendingCloseReason('user_closed')
      return
    }
    if (actionWasCompleted) {
      onActionCompleted()
    } else {
      onUserClose()
    }
  }

  const requestActionCompletedClose = () => {
    setActionWasCompleted(true)
    if (hasUnsentNote) {
      setPendingCloseReason('action_completed')
      return
    }
    onActionCompleted()
  }

  const handleNoteDraftChange = (draft: string) => {
    onUnsentNoteChange?.(Boolean(draft.trim()))

    if (draft.trim()) {
      setNoteDraft(draft)
    } else {
      removeNoteDraft()
      setPendingCloseReason(undefined)
    }
  }

  const keepEditingNote = () => {
    setPendingCloseReason(undefined)
    blockedNavigation?.reset()
    setTimeout(() => document.getElementById(`task-note-${task.id}`)?.focus(), 0)
  }

  const discardNoteAndContinue = () => {
    handleNoteDraftChange('')

    if (blockedNavigation) {
      if (actionWasCompleted) onActionCompletedNavigation()
      blockedNavigation.proceed()
    } else if (pendingCloseReason === 'action_completed' || actionWasCompleted) {
      onActionCompleted()
    } else {
      onUserClose()
    }
  }

  return (
    <TaskDrawerFrame
      title={task.title}
      titleContext={task.resource_name ?? client?.name}
      onClose={requestUserClose}
    >
      {(pendingCloseReason || blockedNavigation) && (
        <DiscardNoteConfirmation
          onKeepEditing={keepEditingNote}
          onDiscard={discardNoteAndContinue}
        />
      )}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto min-[720px]:grid min-[720px]:grid-cols-[minmax(0,1fr)_352px] min-[720px]:gap-6 min-[720px]:overflow-hidden min-[720px]:pl-6"
        data-testid="task-drawer-layout"
      >
        <div
          className="order-2 min-h-0 shrink-0 space-y-8 overflow-y-auto px-6 py-6 min-[720px]:order-1 min-[720px]:flex min-[720px]:flex-col min-[720px]:gap-8 min-[720px]:space-y-0 min-[720px]:px-0"
          data-testid="task-drawer-main"
        >
          <div className="shrink-0">
            <TaskWorkspace
              task={task}
              client={client}
              canWork={canWork}
              onClose={requestActionCompletedClose}
            />
          </div>
          <TaskHistory
            task={task}
            events={events}
            noteDraft={noteDraft}
            onNoteDraftChange={handleNoteDraftChange}
          />
        </div>

        <aside
          className="order-1 min-h-0 shrink-0 bg-sand-25 p-6 min-[720px]:order-2 min-[720px]:overflow-y-auto"
          aria-label="Task details"
          data-testid="task-drawer-sidebar"
        >
          {task.status === 'open' &&
            (task.capabilities.can_claim || task.capabilities.can_release) && (
              <div className="sticky top-0 z-[1] bg-sand-25 pb-6" data-testid="task-ownership-rail">
                <TaskClaimControl task={task} currentUserId={currentUserId} />
              </div>
            )}
          <div data-testid="task-drawer-details">
            <TaskMetadataCard task={task} />
            <TaskClientCard task={task} client={client} />
            <TaskGuardianCard task={task} client={client} />
          </div>
        </aside>
      </div>
    </TaskDrawerFrame>
  )
}

function DiscardNoteConfirmation({
  onKeepEditing,
  onDiscard,
}: {
  onKeepEditing: () => void
  onDiscard: () => void
}) {
  const titleId = useId()
  const descriptionId = useId()

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onKeepEditing()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-30 bg-black/30" />
        <Dialog.Viewport className="fixed inset-0 z-40 flex items-center justify-center p-6">
          <Dialog.Popup
            className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg"
            role="alertdialog"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
          >
            <Dialog.Title id={titleId} className="text-lg font-semibold text-charcoal">
              Discard unsent note?
            </Dialog.Title>
            <Dialog.Description id={descriptionId} className="mt-2 text-sm text-charcoal-50">
              Your note hasn’t been submitted. You’ll lose it if you close this task.
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={onKeepEditing} autoFocus>
                Keep editing
              </Button>
              <Button onClick={onDiscard}>Discard note</Button>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
